"use client";

import Image from "next/image";
import * as React from "react";
import { useInView } from "framer-motion";
import { X } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type ImageGalleryItem = {
  src: string;
  alt: string;
  title?: string;
  subtitle?: string;
  caption?: string;
  tag?: string;
  ratio?: number;
  priority?: boolean;
};

type ImageGalleryProps = {
  items: ImageGalleryItem[];
  className?: string;
};

export function ImageGallery({ items, className }: ImageGalleryProps) {
  const columnCount = useGalleryColumnCount();
  const columns = React.useMemo(
    () => balanceIntoColumns(items, columnCount),
    [items, columnCount],
  );

  return (
    <div className={cn("relative w-full", className)}>
      <div
        className={cn(
          "mx-auto grid w-full max-w-6xl gap-5",
          columnCount === 1 && "grid-cols-1",
          columnCount === 2 && "grid-cols-2",
          columnCount === 3 && "grid-cols-3",
        )}
      >
        {columns.map((column, columnIndex) => (
          <div key={columnIndex} className="flex min-w-0 flex-col gap-5">
            {column.map((item) => (
              <AnimatedImage key={item.src} item={item} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function useGalleryColumnCount() {
  const [columnCount, setColumnCount] = React.useState(3);

  React.useEffect(() => {
    const updateColumnCount = () => {
      if (window.innerWidth >= 1024) {
        setColumnCount(3);
      } else if (window.innerWidth >= 640) {
        setColumnCount(2);
      } else {
        setColumnCount(1);
      }
    };

    updateColumnCount();
    window.addEventListener("resize", updateColumnCount);

    return () => {
      window.removeEventListener("resize", updateColumnCount);
    };
  }, []);

  return columnCount;
}

function balanceIntoColumns(items: ImageGalleryItem[], count: number) {
  const columns = Array.from({ length: count }, () => [] as ImageGalleryItem[]);
  const columnHeights = Array.from({ length: count }, () => 0);

  for (const item of items) {
    const targetColumn = columnHeights.indexOf(Math.min(...columnHeights));
    columns[targetColumn].push(item);
    columnHeights[targetColumn] += estimateCardHeight(item);
  }

  return columns;
}

function estimateCardHeight(item: ImageGalleryItem) {
  const imageRatio = Math.max(item.ratio ?? 0.8, 0.2);
  const imageHeight = 1 / imageRatio;
  const textHeight = item.title || item.subtitle || item.caption ? 0.42 : 0;
  const tagHeight = item.tag ? 0.12 : 0;

  return imageHeight + textHeight + tagHeight;
}

function AnimatedImage({ item }: { item: ImageGalleryItem }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px -12% 0px" });
  const [isLoading, setIsLoading] = React.useState(true);
  const title = item.title ?? item.alt;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`${title} 크게 보기`}
          className="block w-full rounded-md text-left outline-none transition focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <Card
            ref={ref}
            className="group overflow-hidden border border-border/70 bg-card/95 shadow-sm transition duration-300 ease-out hover:-translate-y-1 hover:shadow-xl"
          >
            <CardContent className="p-0">
              <AspectRatio
                ratio={item.ratio ?? 4 / 5}
                className="relative overflow-hidden bg-muted"
              >
                <Image
                  alt={item.alt}
                  src={item.src}
                  fill
                  sizes="(min-width: 1024px) 30vw, (min-width: 640px) 46vw, 100vw"
                  quality={68}
                  className={cn(
                    "object-cover opacity-0 transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.035]",
                    isInView && !isLoading && "opacity-100",
                  )}
                  priority={item.priority}
                  onLoad={() => setIsLoading(false)}
                />
              </AspectRatio>
            </CardContent>

            {item.title || item.subtitle || item.caption ? (
              <CardHeader className="gap-2 p-4 pb-3">
                {item.title ? (
                  <CardTitle className="break-keep text-base font-semibold leading-6">
                    {item.title}
                  </CardTitle>
                ) : null}
                {item.subtitle ? (
                  <p className="break-keep text-sm font-bold leading-5 text-[#8c0000]">
                    {item.subtitle}
                  </p>
                ) : null}
                {item.caption ? (
                  <CardDescription className="break-keep whitespace-pre-line text-sm leading-6">
                    {item.caption}
                  </CardDescription>
                ) : null}
              </CardHeader>
            ) : null}

            {item.tag ? (
              <CardFooter className="justify-between px-4 pb-4 pt-0">
                <Badge variant="outline">{item.tag}</Badge>
              </CardFooter>
            ) : null}
          </Card>
        </button>
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className="max-h-[calc(100svh-1rem)] max-w-[min(1180px,calc(100vw-1rem))] overflow-hidden rounded-md border border-[#fff7d6]/18 bg-[#050505] p-0 text-[#fff7d6] shadow-[0_28px_90px_rgba(0,0,0,0.72)] sm:max-w-[min(1180px,calc(100vw-2rem))]"
      >
        <DialogClose className="absolute right-3 top-3 z-20 inline-flex size-10 items-center justify-center rounded-full border border-[#fff7d6]/25 bg-[#050505]/72 text-[#fff7d6] backdrop-blur transition hover:bg-[#ffe100] hover:text-[#050505] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffe100]">
          <X className="size-5" aria-hidden="true" />
          <span className="sr-only">닫기</span>
        </DialogClose>
        <figure className="grid max-h-[calc(100svh-1rem)] grid-rows-[minmax(0,1fr)_auto]">
          <div className="relative min-h-[54vh] bg-[#050505] sm:h-[min(78vh,780px)]">
            <Image
              alt={item.alt}
              src={item.src}
              fill
              sizes="(min-width: 1180px) 1120px, calc(100vw - 2rem)"
              quality={82}
              className="object-contain"
            />
          </div>
          <DialogHeader className="gap-2 border-t border-[#fff7d6]/12 bg-[#fffdf2] px-5 py-4 text-left text-[#050505] sm:px-6">
            <p className="text-xs font-black text-[#8c0000]">센터 사진</p>
            <DialogTitle className="break-keep text-xl font-black leading-tight text-[#050505] sm:text-2xl">
              {title}
            </DialogTitle>
            {item.subtitle ? (
              <p className="break-keep text-sm font-black leading-5 text-[#8c0000]">
                {item.subtitle}
              </p>
            ) : null}
            {item.caption ? (
              <DialogDescription className="break-keep whitespace-pre-line text-sm font-semibold leading-6 text-[#4d3a00]">
                {item.caption}
              </DialogDescription>
            ) : null}
          </DialogHeader>
        </figure>
      </DialogContent>
    </Dialog>
  );
}
